import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import apiClient from '@/apiClient'
import type {
  CreateCustomerBody,
  CustomerBalance,
  UpdateCustomerBody,
} from '@/types/backend'

export type Customer = {
  id: string
  name: string
  contact?: string
  phone?: string | null
  email?: string | null
  whatsappOptIn?: boolean
  isWalkIn?: boolean
  createdAt?: string
  updatedAt?: string
}

export type CustomersPageResult = {
  data: Customer[]
  total: number
  page: number
  pageSize: number
}

export const useCustomersQuery = (
  params: {
    q?: string
    page: number // 1-based
    pageSize: number
    order?: 'asc' | 'desc'
  },
  enabled = true
) =>
  useQuery({
    queryKey: ['customers', 'page', params],
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data } = await apiClient.get<CustomersPageResult>('/customers', {
        params: {
          q: params.q || undefined,
          page: params.page,
          pageSize: params.pageSize,
          order: params.order ?? 'desc',
        },
      })
      return data
    },
  })

export const useCustomerQuery = (id?: string, enabled = !!id) => {
  return useQuery({
    queryKey: ['customers', id],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<Customer>(`/customers/${id}`)
      return data
    },
  })
}

export const useCreateCustomerMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateCustomerBody) => {
      const { data } = await apiClient.post<Customer>(`/customers`, payload)
      return data
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['customers'] })
      qc.setQueryData(['customers', created.id], created)
    },
  })
}

// --- UPDATE
export const useUpdateCustomerMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: string; data: UpdateCustomerBody }) => {
      const { id, data: payload } = vars
      const { data } = await apiClient.patch<Customer>(
        `/customers/${id}`,
        payload
      )
      return data
    },
    onSuccess: (updated, vars) => {
      qc.setQueryData(['customers', vars.id], updated)
      qc.invalidateQueries({ queryKey: ['customers'] })
    },
  })
}

// --- DELETE
export const useDeleteCustomerMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/customers/${id}`)
      return { ok: true as const }
    },
    onSuccess: (_, id) => {
      qc.removeQueries({ queryKey: ['customers', id] })
      qc.invalidateQueries({ queryKey: ['customers'] })
    },
  })
}

export const useWalkInCustomer = () =>
  useQuery({
    queryKey: ['customers', 'walk-in'],
    queryFn: async () => {
      const { data } = await apiClient.get<Customer>('/customers/walk-in')
      return data
    },
    staleTime: Infinity,
  })

export const useCustomerBalanceQuery = (id?: string, enabled = !!id) => {
  return useQuery({
    queryKey: ['customers', id, 'balance'],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<CustomerBalance>(
        `/customers/${id}/balance`
      )
      return data
    },
  })
}

export type AccountFilters = {
  from?: string // ISO
  to?: string // ISO
  dir?: 'asc' | 'desc'
  pageSize?: number
}

export type CustomerAccountPageResult = {
  customer: { id: string; name: string; contact?: string | null }
  opening: number
  closing: number
  lines: Array<{
    id: string
    date: string
    kind: 'BILL' | 'PAYMENT'
    billId?: string | null
    description?: string | null
    debit: number
    credit: number
    amount: number
    balance: number
  }>
  pagination: { page: number; pageSize: number; total: number }
}

/**
 * Retourne page par page les lignes du compte d’un client.
 * getNextPageParam calcule la page suivante si (page * pageSize < total).
 */
export const useCustomerAccountQuery = (
  id?: string,
  params: { page: number; pageSize: number } & AccountFilters = {
    page: 1,
    pageSize: 100,
  },
  enabled = !!id
) =>
  useQuery({
    queryKey: ['customers', id, 'account', params],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<CustomerAccountPageResult>(
        `/customers/${id}/account`,
        {
          params: {
            from: params.from,
            to: params.to,
            dir: params.dir ?? 'asc',
            page: params.page,
            pageSize: Math.min(Math.max(params.pageSize, 1), 500),
          },
        }
      )
      return data
    },
  })
